import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PushPinIcon from '@mui/icons-material/PushPin';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import PageHeader from '../components/common/PageHeader';
import DiskussionRichEditor from '../components/diskussion/DiskussionRichEditor';
import { usePermissions } from '../hooks/usePermissions';
import { diskussionService, DiskussionAnkerIn } from '../services/diskussionService';
import type { DiskussionThreadList } from '../types';
import * as P from '../constants/permissions';

const isEmptyHtml = (s: string): boolean => {
  const t = s.replace(/<p>\s*<\/p>|<br\s*\/?>|&nbsp;|\s/g, '').trim();
  return t === '' || t === '<p></p>';
};

function relativeTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return formatDistanceToNow(d, { addSuffix: true, locale: de });
}

function authorName(autor: DiskussionThreadList['autor']): string {
  return autor ? `${autor.vorname} ${autor.nachname}` : 'Unbekannt';
}

const DiskussionCategoryPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const kategorieId = Number(id);
  const [searchParams] = useSearchParams();
  const { hasPermission } = usePermissions();

  const refType = searchParams.get('refType') || undefined;
  const refIdParam = searchParams.get('refId');
  const refId = refIdParam != null && refIdParam !== '' ? Number(refIdParam) : undefined;
  const hof_jahr = searchParams.get('hof_jahr') ? Number(searchParams.get('hof_jahr')) : undefined;
  const hof_monat = searchParams.get('hof_monat') ? Number(searchParams.get('hof_monat')) : undefined;
  const hof_mitarbeiter_id = searchParams.get('hof_mitarbeiter_id')
    ? Number(searchParams.get('hof_mitarbeiter_id'))
    : undefined;

  const [threads, setThreads] = useState<DiskussionThreadList[]>([]);
  const [kategorieName, setKategorieName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [titel, setTitel] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!Number.isFinite(kategorieId)) return;
    setLoading(true);
    try {
      const data = await diskussionService.listThreads(kategorieId, {
        ref_type: refType,
        ref_id: refId,
        hof_jahr,
        hof_monat,
        hof_mitarbeiter_id,
      });
      setThreads(data);
    } finally {
      setLoading(false);
    }
  }, [kategorieId, refType, refId, hof_jahr, hof_monat, hof_mitarbeiter_id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let c = false;
    (async () => {
      if (!Number.isFinite(kategorieId)) return;
      try {
        const rows = await diskussionService.listKategorien();
        const k = rows.find((x) => x.id === kategorieId);
        if (!c) setKategorieName(k?.name ?? null);
      } catch {
        if (!c) setKategorieName(null);
      }
    })();
    return () => {
      c = true;
    };
  }, [kategorieId]);

  const defaultAnker = (): DiskussionAnkerIn[] => {
    if (!refType) return [];
    if (refType === 'hof_uebersicht') return [{ ref_type: 'hof_uebersicht' }];
    if (refType === 'hof_monat_mitarbeiter') {
      return [
        {
          ref_type: 'hof_monat_mitarbeiter',
          ref_id: refId ?? hof_mitarbeiter_id,
          hof_jahr,
          hof_monat,
          hof_mitarbeiter_id: hof_mitarbeiter_id ?? refId,
        },
      ];
    }
    if (refId == null) return [];
    return [{ ref_type: refType, ref_id: refId }];
  };

  const handleCreate = async () => {
    if (!titel.trim() || isEmptyHtml(body)) return;
    setSaving(true);
    try {
      await diskussionService.createThread(kategorieId, {
        titel: titel.trim(),
        first_post_body: body,
        anker: defaultAnker(),
      });
      setOpen(false);
      setTitel('');
      setBody('');
      await load();
    } finally {
      setSaving(false);
    }
  };

  const canPost = hasPermission(P.DISKUSSION_POST);
  const filtered = Boolean(refType || refId != null);
  const title = kategorieName?.trim() || 'Kategorie';

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        title={title}
        subtitle="Threads nach letzter Aktivität. Angeheftete Themen stehen oben."
        breadcrumbs={[
          { label: 'Diskussion', path: '/diskussion' },
          { label: title },
        ]}
        onAdd={
          canPost
            ? () => {
                const hint = searchParams.get('contextLabel');
                setTitel(hint?.trim() || '');
                setBody('');
                setOpen(true);
              }
            : undefined
        }
        addLabel="Neuer Thread"
      />
      {filtered && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {refType && (
            <Chip
              size="small"
              label={`Anker: ${refType}`}
              sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal' } }}
            />
          )}
          {refId != null && <Chip size="small" label={`ID ${refId}`} />}
        </Box>
      )}
      {loading ? (
        <CircularProgress />
      ) : threads.length === 0 ? (
        <Typography color="text.secondary">
          {filtered ? 'Keine Threads zu diesem Anker.' : 'Noch keine Threads in dieser Kategorie.'}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              px: 2,
              color: 'text.secondary',
              gap: 2,
            }}
          >
            <Typography variant="caption" sx={{ flex: 1 }}>
              Thema
            </Typography>
            <Typography variant="caption" sx={{ width: 168, textAlign: 'right', flexShrink: 0 }}>
              Aktivität
            </Typography>
          </Box>
          {threads.map((t) => {
            const when = relativeTime(t.aktualisiert_am);
            return (
              <Card
                key={t.id}
                variant="outlined"
                sx={{
                  minWidth: 0,
                  borderLeft: t.pinned ? 3 : undefined,
                  borderLeftColor: t.pinned ? 'primary.main' : undefined,
                }}
              >
                <CardActionArea component={RouterLink} to={`/diskussion/thread/${t.id}`}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      p: 1.5,
                      px: 2,
                      minWidth: 0,
                      flexWrap: { xs: 'wrap', md: 'nowrap' },
                    }}
                  >
                    <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        {t.pinned && (
                          <Tooltip title="Angeheftet">
                            <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
                              <PushPinIcon fontSize="small" color="primary" aria-label="Angeheftet" />
                            </Box>
                          </Tooltip>
                        )}
                        {t.locked && (
                          <Tooltip title="Gesperrt">
                            <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
                              <LockIcon fontSize="small" color="warning" aria-label="Gesperrt" />
                            </Box>
                          </Tooltip>
                        )}
                        {t.watching && (
                          <Tooltip title="Thread wird beobachtet">
                            <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
                              <NotificationsIcon fontSize="small" color="primary" aria-label="Beobachtet" />
                            </Box>
                          </Tooltip>
                        )}
                        <Typography
                          component="h2"
                          sx={{
                            fontWeight: t.new_posts_count > 0 ? 700 : 600,
                            fontSize: '1rem',
                            minWidth: 0,
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {t.titel}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, overflowWrap: 'anywhere' }}>
                        {authorName(t.autor)}
                        {t.posts_count === 1 ? ' · 1 Beitrag' : ` · ${t.posts_count} Beiträge`}
                      </Typography>
                      {(t.anker.length > 0 || t.new_posts_count > 0) && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.75 }}>
                          {t.new_posts_count > 0 && (
                            <Chip size="small" color="primary" label={`+${t.new_posts_count} neue Beiträge`} />
                          )}
                          {t.anker.map((a, i) => (
                            <Chip
                              key={`${a.ref_type}-${a.ref_id}-${i}`}
                              size="small"
                              variant="outlined"
                              label={a.label}
                              sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal' } }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ width: { md: 168 }, flexShrink: 0, textAlign: { xs: 'left', md: 'right' }, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                        {when || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Letzte Aktivität
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>
            );
          })}
        </Box>
      )}

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Neuer Thread</DialogTitle>
        <DialogContent>
          <TextField
            label="Titel"
            fullWidth
            margin="normal"
            value={titel}
            onChange={(e) => setTitel(e.target.value)}
          />
          <Box sx={{ mt: 2, minWidth: 0 }}>
            <DiskussionRichEditor
              value={body}
              onChange={setBody}
              disabled={saving}
              label="Erster Beitrag"
              placeholder="Beitrag verfassen… @m… erwähnt Kolleg:innen; Bilder per Strg+V einfügen"
              allowChecklist
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={handleCreate} variant="contained" disabled={saving || !titel.trim() || isEmptyHtml(body)}>
            {saving ? <CircularProgress size={22} /> : 'Anlegen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DiskussionCategoryPage;
